import { createContext, useContext, useEffect, useRef, useState } from "react";
import {
	Chart as ChartJS,
	LinearScale,
	CategoryScale,
	TimeScale,
	BarElement,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
	Title,
	LineController,
	BarController,
	Decimation,
	Interaction,
} from "chart.js";
import { getRelativePosition } from "chart.js/helpers";
import moment from "moment";
import zoomPlugin from "chartjs-plugin-zoom";
import annotationPlugin from "chartjs-plugin-annotation";
import dataLabelPlugin from "chartjs-plugin-datalabels";
import "chartjs-adapter-moment";
import { Chart } from "react-chartjs-2";

import "../../styles/StackPlot.css";
import { colors } from "../../styles/colors";
import { isNumeric } from "../../util/globals";
import { DataValidationContext } from "./DataValidation";

ChartJS.register(
	LinearScale,
	CategoryScale,
	TimeScale,
	BarElement,
	PointElement,
	LineElement,
	Legend,
	Tooltip,
	Title,
	LineController,
	BarController,
	zoomPlugin,
	annotationPlugin,
	Decimation,
);

const Y_AXIS_LABEL_WIDTH = 50;

const STACKPLOT_PAGE_HEIGHT = 1100;
const STACKPLOT_PAGE_WIDTH = 850;
const STACKPLOT_PAGE_MARGIN = 25;
const STACKPLOT_TITLE_HEIGHT = 50;
const STACKPLOT_LABEL_HEIGHT = 15;
const STACKPLOT_LABEL_BOTTOM_MARGIN = -3;
const STACKPLOT_LABEL_EFFECTIVE_HEIGHT =
	STACKPLOT_LABEL_HEIGHT + STACKPLOT_LABEL_BOTTOM_MARGIN;
const STACKPLOT_LABEL_FONT_SIZE = "12px";
const STACKPLOT_X_AXIS_HEIGHT = 40;
const STACKPLOT_FOOTNOTE_HEIGHT = 25;
const STACKPLOT_INNER_PAGE_HEIGHT =
	STACKPLOT_PAGE_HEIGHT - 2 * STACKPLOT_PAGE_MARGIN;
const STACKPLOT_INNER_PAGE_WIDTH =
	STACKPLOT_PAGE_WIDTH - 2 * STACKPLOT_PAGE_MARGIN;
const STACKPLOT_WRAPPER_BORDER_WIDTH = 1;

const STACKPLOT_AXIS_FONT = {
	size: 10,
};

const STACKPLOT_AXIS_MAJOR_FONT = {
	...STACKPLOT_AXIS_FONT,
	weight: "bold",
};

Interaction.modes.myCustomMode = function (
	chart,
	e,
	options,
	useFinalPosition,
) {
	const position = getRelativePosition(e, chart);

	const items = [];
	const itemPerDataset = {};
	const originalLog = console.log;
	console.log = () => { };
	Interaction.evaluateInteractionItems(
		chart,
		"x",
		position,
		(element, datasetIndex, index) => {
			if (element.inXRange(position.x, useFinalPosition)) {
				itemPerDataset[datasetIndex] = { element, datasetIndex, index };
			}
		},
	);
	console.log = originalLog;
	for (let di of Object.keys(itemPerDataset).sort()) {
		items.push(itemPerDataset[di]);
	}
	return items;
};

const defaultPlotOptions = {
	responsive: true,
	animation: false,
	devicePixelRatio: 2,
	interaction: {
		mode: "myCustomMode",
		intersect: false,
	},
	layout: {
		padding: {
			top: 0,
			bottom: 0,
			left: 0,
			right: Y_AXIS_LABEL_WIDTH,
		},
	},
	datasets: {
		line: {
			spanGaps: false,
		},
	},
	plugins: {
		legend: {
			display: false,
		},
		tooltip: {
			position: "average",
		},
		decimation: {
			enabled: true,
		},
		zoom: {
			zoom: {
				wheel: {
					enabled: true,
				},
				pinch: {
					enabled: true,
				},
				mode: "xy",
			},
			pan: {
				enabled: true,
				mode: "xy",
			},
			limits: {
				x: {
					min: "original",
					max: "original",
					minRange: 60 * 60 * 1000,
				},
			},
		},
		annotation: {
			annotations: { cursor1: null, cursor2: null },
		},
	},
	scales: {
		x: {
			type: "time",
			time: {
				minUnit: "hour",
			},
			ticks: {
				display: false,
			},
			offset: false,
			grid: {
				offset: false,
				color: colors["ars-neutral-750"],
			},
		},
		y: {
			type: "linear",
			suggestedMin: 0,
			suggestedMax: 1,
			ticks: {
				font: STACKPLOT_AXIS_FONT,
			},
			grid: {
				color: colors["ars-neutral-750"],
			},
		},
	},
	elements: {
		bar: {
			borderWidth: 0,
		},
		point: {
			hitRadius: 10,
		},
	},
	events: [
		"mousemove",
		"mouseout",
		"click",
		"touchstart",
		"touchmove",
		"dblclick",
		"keydown",
		"blur",
	],
	maintainAspectRatio: false,
	parsing: false,
};

const xAxisOptions = {
	responsive: true,
	animation: false,
	devicePixelRatio: 2,
	layout: {
		padding: {
			top: -100,
			bottom: 0,
			left: Y_AXIS_LABEL_WIDTH,
			right: Y_AXIS_LABEL_WIDTH,
		},
	},
	scales: {
		x: {
			type: "time",
			position: "bottom",
			time: {
				unit: "hour",
				displayFormats: {
					minute: "HH:mm",
					hour: "HH:mm",
					day: "MMM D",
					year: "YYYY",
				},
			},
			ticks: {
				major: {
					enabled: true,
				},
				font: (context) => {
					if (context.tick.major) return STACKPLOT_AXIS_MAJOR_FONT;
					else return STACKPLOT_AXIS_FONT;
				},
				minRotation: 90,
				maxRotation: 90,
				callback: (value, index, ticks) => {
					const tick = ticks[index];
					const mmnt = moment(tick.value);
					if (tick.major) {
						if (mmnt.format("MM/DD HH:mm") === "01/01 00:00") {
							return mmnt.format("YYYY");
						} else {
							return mmnt.format("MM/DD");
						}
					} else {
						return mmnt.format("HH:mm");
					}
				},
			},
			grid: {
				display: true,
				color: colors["ars-neutral-750"],
			},
			border: {
				display: false,
			},
		},
		y: {
			display: false,
		},
	},
	plugins: {
		zoom: {
			zoom: {
				mode: "x",
			},
			pan: {
				enabled: true,
				mode: "x",
			},
			limits: {
				x: {
					min: "original",
					max: "original",
					minRange: 60 * 60 * 1000,
				},
			},
		},
	},
	maintainAspectRatio: false,
};

const backgroundColorPlugin = {
	id: "customCanvasBackgroundColor",
	beforeDraw: (chart, args, options) => {
		const { ctx } = chart;
		ctx.save();
		ctx.globalCompositeOperation = "destination-over";
		ctx.fillStyle = options.color || "transparent";
		ctx.fillRect(0, 0, chart.width, chart.height);
		ctx.restore();
	},
};

const ValidationPlotContext = createContext(null);

const GREEN_CODES = ["V", "VA", "VZ"];
const YELLOW_CODES = ["MT", "ZS"];
const RED_CODES = ["PF", "IM"];

function ValidationPlot({
	data,
	config,
	plugins = [],
	dateRange = null,
	stackPlotRef = null,
	printable = false,
	bubbleChartRefs = null,
	callbacks = null,
	highlighted,
	setHighlighted,
	scrollTableOnCursor,
	onClearCursors,
	autoYAxis,
	fixedYScales
}) {
	const [chartRefs, setChartRefs] = useState(new Map());
	const [chartData, setChartData] = useState({});
	const [chartZooms, setChartZooms] = useState(new Map([["x", null]]));
	const [cursors, setCursors] = useState(new Map());
	const [originalScales, setOriginalScales] = useState(new Map());
	const [showCalDataLabels, setCalDataLabels] = useState(true);
	const [plotHighlights, setPlotHighlights] = useState({});

	const { isHighlighted } = useContext(DataValidationContext);

	const toggleCalDataLabels = () => setCalDataLabels(!showCalDataLabels);

	function updateRange(scale, { min, max }, limits, zoom = false) {
		const { id, axis, options: scaleOpts } = scale;

		const scaleLimits = limits[id] || {};
		const { minRange = 0 } = scaleLimits;
		const minLimit =
			!scale.isHorizontal() || scaleLimits["min"] === undefined
				? -Infinity
				: scaleLimits["min"];
		const maxLimit =
			!scale.isHorizontal() || scaleLimits["max"] === undefined
				? Infinity
				: scaleLimits["max"];

		const range = zoom
			? Math.max(max - min, minRange)
			: scale.max - scale.min;
		const offset = (range - max + min) / 2;
		min -= offset;
		max += offset;

		if (min < minLimit) {
			min = minLimit;
			max = Math.min(minLimit + range, maxLimit);
		} else if (max > maxLimit) {
			max = maxLimit;
			min = Math.max(maxLimit - range, minLimit);
		}
		scaleOpts.min = min;
		scaleOpts.max = max;

		// return true if the scale range is changed
		return scale.parse(min) !== scale.min || scale.parse(max) !== scale.max;
	}

	zoomPlugin.panFunctions.default = (scale, delta, limits) => {
		const { min: prevStart, max: prevEnd, id, options } = scale;
		if (scale.chart.isCalPlot && id === "y1") return false;
		limits = scale.chart.originalScales;
		if (!limits) return false;
		const newMin = scale.getValueForPixel(
			scale.getPixelForValue(prevStart) - delta,
		);
		const newMax = scale.getValueForPixel(
			scale.getPixelForValue(prevEnd) - delta,
		);
		return updateRange(scale, { min: newMin, max: newMax }, limits, false);
	};

	useEffect(() => {
		if (chartRefs && bubbleChartRefs) {
			bubbleChartRefs(chartRefs);
		}
	}, [chartRefs]);

	useEffect(() => {
		if (stackPlotRef?.current) {
			stackPlotRef.current.addEventListener("wheel", handleWheel, {
				passive: false,
			});
		}
	}, [stackPlotRef]);

	useEffect(() => {
		if (dateRange?.min && dateRange?.max) {
			zoomAll(
				moment(dateRange.min, "YYYY-MM-DDTHH:mm").valueOf(),
				moment(dateRange.max, "YYYY-MM-DDTHH:mm").valueOf(),
			);
		}
	}, [dateRange]);

	useEffect(() => {
		chartZooms.clear();
		cursors.clear();
		originalScales.clear();
	}, [config]);

	useEffect(() => {
		if (!config?.charts || !data?._timestamps) return;

		let labels = getLabelsFromTimestamps(data._timestamps);
		const newChartData = {};
		config.charts.forEach((chartConfig, index) => {
			const datasets = [];

			if (chartConfig.parameters?.length) {
				if (isCalPlot()) {
					for (let parameter of chartConfig.parameters) {
						const calPar = [parameter.parCode, parameter.calType, parameter.valType];
						const valCalPar = [parameter.parCode + "_VAL", parameter.calType, parameter.valType];
						if (
							(chartConfig.hasRightSide || parameter.side === "L") &&
							data[calPar]
						) {
							datasets.push(
								generateCalDataset(
									data[calPar],
									parameter.parCode,
									parameter.calType,
									parameter.valType,
									parameter.unit,
									parameter.type,
									parameter.color +
									(data[valCalPar]
										? "66"
										: ""),
									parameter.symbol,
									parameter.rawPrec,
									data._timestamps,
									parameter.side === "R" ? "y1" : "y",
								),
							);

							if (data[valCalPar]) {
								datasets.push(
									generateCalDataset(
										data[valCalPar],
										parameter.parCode + "_VAL",
										parameter.calType,
										parameter.valType,
										parameter.unit,
										parameter.type,
										parameter.color,
										parameter.symbol,
										parameter.rawPrec,
										data._timestamps,
										parameter.side === "R" ? "y1" : "y",
										1,
									),
								);
							}
						}
					}
				} else {
					for (let parameter of chartConfig.parameters) {
						if (
							(chartConfig.hasRightSide || parameter.side === "L") &&
							data[parameter.parCode]
						) {
							datasets.push(
								generateDataset(
									data[parameter.parCode],
									parameter.parCode,
									parameter.unit,
									parameter.type,
									parameter.color +
									(data[parameter.parCode + "_VAL"]
										? "66"
										: ""),
									parameter.symbol,
									parameter.rawPrec,
									data._timestamps,
									parameter.side === "R" ? "y1" : "y",
								),
							);

							if (data[parameter.parCode + "_VAL"]) {
								datasets.push(
									generateDataset(
										data[parameter.parCode + "_VAL"],
										parameter.parCode + "_VAL",
										parameter.unit,
										parameter.type,
										parameter.color,
										parameter.symbol,
										parameter.rawPrec,
										data._timestamps,
										parameter.side === "R" ? "y1" : "y",
										1,
									),
								);
							}
						}
					}
				}
				
			}

			newChartData[index] = { datasets, labels };
		});
		setChartData(newChartData);
	}, [data]);

	useEffect(() => {
		originalScales.clear();
		chartZooms.clear();
	}, [autoYAxis, fixedYScales]);

	const generatePages = () => {
		if (!(config?.charts && data?._timestamps)) return null;

		const pages = [];
		const chartSpacing =
			(config.chartSpacing || 20) -
			2 * STACKPLOT_WRAPPER_BORDER_WIDTH -
			STACKPLOT_LABEL_EFFECTIVE_HEIGHT;

		let plots = [];
		let remainingSpace =
			STACKPLOT_INNER_PAGE_HEIGHT -
			(STACKPLOT_TITLE_HEIGHT +
				STACKPLOT_X_AXIS_HEIGHT +
				(config.footnote ? STACKPLOT_FOOTNOTE_HEIGHT : 0));
		let xIndex = 1;
		config.charts.forEach((chartConfig, index) => {
			let chartHighlights = {};

			if (chartConfig.parameters && chartConfig.parameters.length) {
				const parCodes = [];
				for (let parameter of chartConfig.parameters) {
					if (
						(chartConfig.hasRightSide || parameter.side === "L") &&
						data[parameter.parCode]
					) {
						parCodes.push(parameter.parCode);
					}
				}
				chartHighlights = getAnnotationsFromHighlighted(parCodes);
			}

			if (remainingSpace < chartConfig.chartHeight + chartSpacing) {
				plots.push(
					<StackPlotXAxis
						data={{
							datasets: [],
							labels: getLabelsFromTimestamps(data._timestamps),
						}}
						xIndex={xIndex}
						bufferSize={remainingSpace}
					/>,
				);
				pages.push(plots);
				plots = [];
				remainingSpace =
					STACKPLOT_INNER_PAGE_HEIGHT -
					(STACKPLOT_X_AXIS_HEIGHT +
						(config.footnote ? STACKPLOT_FOOTNOTE_HEIGHT : 0));
				xIndex++;
			}
			plots.push(
				<StackPlotChart
					config={chartConfig}
					data={getChartData(index)}
					options={defaultPlotOptions}
					chartIndex={index}
					chartSpacing={chartSpacing}
					popoverCallback={callbacks?.openPopover || null}
					chartHighlights={chartHighlights}
					autoYAxis={autoYAxis}
					fixedYScales={fixedYScales}
				/>,
			);
			remainingSpace -= chartConfig.chartHeight + config.chartSpacing;
		});
		plots.push(
			<StackPlotXAxis
				data={{
					datasets: [],
					labels: getLabelsFromTimestamps(data._timestamps),
				}}
				xIndex={xIndex}
				bufferSize={remainingSpace}
			/>,
		);
		pages.push(plots);

		return pages.map((pagePlots, index) => (
			<StackPlotPage
				title={config.fullTitle}
				footnote={config.footnote}
				pageNumber={index + 1}
				charts={pagePlots}
				numPages={pages.length}
			/>
		));
	};

	const generateCalPages = () => {
		if (!(config?.charts && data?._timestamps)) return null;

		const pages = [];
		const chartSpacing =
			(config.chartSpacing || 20) -
			2 * STACKPLOT_WRAPPER_BORDER_WIDTH -
			STACKPLOT_LABEL_EFFECTIVE_HEIGHT;

		let plots = [];
		let remainingSpace =
			STACKPLOT_INNER_PAGE_HEIGHT -
			(STACKPLOT_TITLE_HEIGHT +
				STACKPLOT_X_AXIS_HEIGHT +
				(config.footnote ? STACKPLOT_FOOTNOTE_HEIGHT : 0));
		let xIndex = 1;
		config.charts.forEach((chartConfig, index) => {
			let chartHighlights = {};

			if (chartConfig.parameters && chartConfig.parameters.length) {
				const calPars = [];
				for (let parameter of chartConfig.parameters) {
					const calPar = [
	                    parameter.parCode,
	                    parameter.calType,
	                    parameter.valType,
	                ];
					if (
						(chartConfig.hasRightSide || parameter.side === "L") &&
						data[calPar]
					) {
						calPars.push(calPar);
					}
				}
				chartHighlights = getAnnotationsFromHighlighted(calPars);
			}

			if (remainingSpace < chartConfig.chartHeight + chartSpacing) {
				plots.push(
					<StackPlotXAxis
						data={{
							datasets: [],
							labels: getLabelsFromTimestamps(data._timestamps),
						}}
						xIndex={xIndex}
						bufferSize={remainingSpace}
					/>,
				);
				pages.push(plots);
				plots = [];
				remainingSpace =
					STACKPLOT_INNER_PAGE_HEIGHT -
					(STACKPLOT_X_AXIS_HEIGHT +
						(config.footnote ? STACKPLOT_FOOTNOTE_HEIGHT : 0));
				xIndex++;
			}
			plots.push(
				<StackPlotChart
					config={chartConfig}
					data={getChartData(index)}
					options={defaultPlotOptions}
					chartIndex={index}
					chartSpacing={chartSpacing}
					popoverCallback={callbacks?.openPopover || null}
					chartHighlights={chartHighlights}
					autoYAxis={autoYAxis}
					fixedYScales={fixedYScales}
				/>,
			);
			remainingSpace -= chartConfig.chartHeight + config.chartSpacing;
		});
		plots.push(
			<StackPlotXAxis
				data={{
					datasets: [],
					labels: getLabelsFromTimestamps(data._timestamps),
				}}
				xIndex={xIndex}
				bufferSize={remainingSpace}
			/>,
		);
		pages.push(plots);

		return pages.map((pagePlots, index) => (
			<StackPlotPage
				title={config.fullTitle}
				footnote={config.footnote}
				pageNumber={index + 1}
				charts={pagePlots}
				numPages={pages.length}
			/>
		));
	};

	// const generateCalPages = () => {
	//     if (!(config?.charts && data?._timestamps)) return null;

	//     const pages = [];
	//     const chartSpacing =
	//         (config.chartSpacing || 20) -
	//         2 * STACKPLOT_WRAPPER_BORDER_WIDTH -
	//         STACKPLOT_LABEL_EFFECTIVE_HEIGHT;

	//     let labels = getLabelsFromTimestamps(data._timestamps);

	//     let plots = [];
	//     let remainingSpace =
	//         STACKPLOT_INNER_PAGE_HEIGHT -
	//         (STACKPLOT_TITLE_HEIGHT +
	//             STACKPLOT_X_AXIS_HEIGHT +
	//             (config.footnote ? STACKPLOT_FOOTNOTE_HEIGHT : 0));
	//     let xIndex = 1;
	//     config.charts.forEach((chartConfig, index) => {
	//         const datasets = [];

	//         if (chartConfig.parameters && chartConfig.parameters.length) {
	//             for (let parameter of chartConfig.parameters) {
	//                 const calPar = [
	//                     parameter.parCode,
	//                     parameter.calType,
	//                     parameter.valType,
	//                 ];
	//                 if (calPar[2] === "PDIFF") {
	//                     const parDataset = generatePercentDiffDataset(
	//                         calPar,
	//                         chartConfig.parameters,
	//                         data._timestamps
	//                     );
	//                     datasets.push(parDataset);
	//                 } else if (calPar[2] === "DIFF") {
	//                     const parDataset = generateDiffDataset(
	//                         calPar,
	//                         chartConfig.parameters,
	//                         data._timestamps
	//                     );
	//                     datasets.push(parDataset);
	//                 } else if (data[calPar]) {
	//                     const parDataset = generateCalDataset(
	//                         data[calPar],
	//                         parameter.parCode,
	//                         parameter.calType,
	//                         parameter.valType,
	//                         parameter.unit,
	//                         parameter.type,
	//                         parameter.color +
	//                             (data[parameter.parCode + "_VAL"] ? "66" : ""),
	//                         parameter.symbol,
	//                         parameter.rawPrec,
	//                         data._timestamps,
	//                         parameter.side === "R" ? "y1" : "y"
	//                     );
	//                     datasets.push(parDataset);
	//                     const valCalPar = [
	//                         parameter.parCode + "_VAL",
	//                         parameter.calType,
	//                         parameter.valType,
	//                     ];
	//                     if (valCalPar in data) {
	//                         const valParDataset = generateCalDataset(
	//                             data[valCalPar],
	//                             parameter.parCode + "_VAL",
	//                             parameter.calType,
	//                             parameter.valType,
	//                             parameter.unit,
	//                             parameter.type,
	//                             parameter.color,
	//                             parameter.symbol,
	//                             parameter.rawPrec,
	//                             data._timestamps,
	//                             parameter.side === "R" ? "y1" : "y",
	//                             1
	//                         );
	//                         datasets.push(valParDataset);
	//                     }
	//                 }
	//             }
	//         }

	//         if (remainingSpace < chartConfig.chartHeight + chartSpacing) {
	//             plots.push(
	//                 <StackPlotXAxis
	//                     data={{ datasets: [], labels }}
	//                     xIndex={xIndex}
	//                     bufferSize={remainingSpace}
	//                 />
	//             );
	//             pages.push(plots);
	//             plots = [];
	//             remainingSpace =
	//                 STACKPLOT_INNER_PAGE_HEIGHT -
	//                 (STACKPLOT_X_AXIS_HEIGHT +
	//                     (config.footnote ? STACKPLOT_FOOTNOTE_HEIGHT : 0));
	//             xIndex++;
	//         }
	//         plots.push(
	//             <StackPlotChart
	//                 config={chartConfig}
	//                 data={getChartData(index)}
	//                 options={defaultPlotOptions}
	//                 chartIndex={index}
	//                 chartSpacing={chartSpacing}
	//                 cursorCallback={callbacks?.cursorCallback || null}
	//             />
	//         );
	//         remainingSpace -= chartConfig.chartHeight + config.chartSpacing;
	//     });
	//     plots.push(
	//         <StackPlotXAxis
	//             data={{ datasets: [], labels }}
	//             xIndex={xIndex}
	//             bufferSize={remainingSpace}
	//         />
	//     );
	//     pages.push(plots);

	//     return pages.map((pagePlots, index) => (
	//         <StackPlotPage
	//             title={config.fullTitle}
	//             footnote={config.footnote}
	//             pageNumber={index + 1}
	//             charts={pagePlots}
	//             numPages={pages.length}
	//         />
	//     ));
	// };

	const getChartData = (index) => {
		return chartData?.[index] || { datasets: [], labels: [] };
	};

	const getLabelsFromTimestamps = (timestamps) => {
		if (!timestamps?.length) return [];
		return timestamps.map((timestamp) => {
			let date = moment.unix(timestamp).seconds(0);
			return date.format("YYYY-MM-DDTHH:mm");
		});
	};

	const getAnnotationsFromCalHighlighted = (rowKeys) => {
		if (!highlighted || !data || !rowKeys) return {};
		const highlightedRanges = [];
		const highlights = {};
		if (Object.keys(highlighted).length) {
			const highlightedTimestamps = new Set();
			for (let timestamp of Object.keys(highlighted)) {
				if (rowKeys.some(rowKey => isHighlighted(rowKey, timestamp))) {
					highlightedTimestamps.add(moment(timestamp).hour(0).minute(0).unix());
				}
			}

			if (highlightedTimestamps.size) {
				const highlightedTimestampsSorted = Array.from(
					highlightedTimestamps,
				);
				highlightedTimestampsSorted.sort();
				let l = 0,
					r;
				const interval = 60*60*24;
				for (let i = 1; i < highlightedTimestampsSorted.length; i++) {
					if (
						highlightedTimestampsSorted[i] -
						highlightedTimestampsSorted[i - 1] !==
						interval*2
					) {
						r = i - 1;
						highlightedRanges.push([
							highlightedTimestampsSorted[l],
							highlightedTimestampsSorted[r] + interval,
						]);
						l = i;
					}
				}
				r = highlightedTimestampsSorted.length - 1;
				highlightedRanges.push([
					highlightedTimestampsSorted[l],
					highlightedTimestampsSorted[r] + interval,
				]);

				for (let i = 0; i < highlightedRanges.length; i++) {
					const range = highlightedRanges[i];
					if (range[0] == range[1]) {
						// 1 data point wide
						highlights["highlight-" + i] = {
							type: "box",
							xMin: 1000 * (range[0] - interval / 4),
							xMax: 1000 * (range[1] + interval / 4),
							yMin: -Infinity,
							yMax: Infinity,
							borderWidth: 0,
							backgroundColor: colors["ars-blue-600"] + "40",
						};
					} else {
						highlights["highlight-" + i] = {
							type: "box",
							xMin: 1000 * range[0],
							xMax: 1000 * range[1],
							yMin: -Infinity,
							yMax: Infinity,
							borderWidth: 0,
							backgroundColor: colors["ars-blue-600"] + "40",
						};
					}
				}
			}
		}

		return highlights;
	}

	const getAnnotationsFromHighlighted = (rowKeys) => {
		if (isCalPlot()) return getAnnotationsFromCalHighlighted(rowKeys);
		if (!highlighted || !data || !rowKeys) return {};
		const highlightedRanges = [];
		const highlights = {};
		if (Object.keys(highlighted).length) {
			const highlightedTimestamps = new Set();
			for (let timestamp of Object.keys(highlighted)) {
				if (rowKeys.some(rowKey => isHighlighted(rowKey, timestamp))) {
					highlightedTimestamps.add(moment(timestamp).unix());
				}
			}

			if (highlightedTimestamps.size) {
				const highlightedTimestampsSorted = Array.from(
					highlightedTimestamps,
				);
				highlightedTimestampsSorted.sort();
				let l = 0,
					r;
				const interval = 60*60;
				for (let i = 1; i < highlightedTimestampsSorted.length; i++) {
					if (
						highlightedTimestampsSorted[i] -
						highlightedTimestampsSorted[i - 1] !==
						interval
					) {
						r = i - 1;
						highlightedRanges.push([
							highlightedTimestampsSorted[l],
							highlightedTimestampsSorted[r] + interval,
						]);
						l = i;
					}
				}
				r = highlightedTimestampsSorted.length - 1;
				highlightedRanges.push([
					highlightedTimestampsSorted[l],
					highlightedTimestampsSorted[r] + interval,
				]);

				for (let i = 0; i < highlightedRanges.length; i++) {
					const range = highlightedRanges[i];
					if (range[0] == range[1]) {
						// 1 data point wide
						highlights["highlight-" + i] = {
							type: "box",
							xMin: 1000 * (range[0] - interval / 4),
							xMax: 1000 * (range[1] + interval / 4),
							yMin: -Infinity,
							yMax: Infinity,
							borderWidth: 0,
							backgroundColor: colors["ars-blue-600"] + "40",
						};
					} else {
						highlights["highlight-" + i] = {
							type: "box",
							xMin: 1000 * range[0],
							xMax: 1000 * range[1],
							yMin: -Infinity,
							yMax: Infinity,
							borderWidth: 0,
							backgroundColor: colors["ars-blue-600"] + "40",
						};
					}
				}
			}
		}

		return highlights;
	};

	const clearCursors = () => {
		cursors.clear();
		for (const chartId of chartRefs.keys()) {
			if (isNaN(chartId)) {
				continue;
			} else {
				const chart = chartRefs.get(chartId)?.current;
				if (!chart) {
					continue;
				}
				const annotations =
					chart.options.plugins.annotation.annotations;
				annotations.cursor1 = undefined;
				annotations.cursor2 = undefined;
				for (let key in annotations) {
					if (key.match(/highlight-.*/)) {
						annotations[key] = undefined;
					}
				}
				chart.update();
			}
		}
		if (onClearCursors) {
			onClearCursors();
		}
	};

	const moveCursor = (chartIndex, cursorId, xPos, xIndex) => {
		if (isCalPlot()) {
			let ts = moment(xPos).hour(0).minute(0);
			cursors.set(cursorId, {
				chartIndex: chartIndex,
				pos: ts.valueOf(),
				index: xIndex,
			});
		} else {
			cursors.set(cursorId, {
				chartIndex: chartIndex,
				pos: xPos,
				index: xIndex,
			});
		}
		if (isCalPlot()) {
			const parCode =
				chartRefs.get(chartIndex).current.data.datasets[0].label.split(' ')[0];
			scrollTableOnCursor(xPos, parCode, xIndex, chartIndex);
		} else {
			const parCode =
				chartRefs.get(chartIndex).current.data.datasets[0].label;
			scrollTableOnCursor(xPos, parCode, xIndex, chartIndex);
		}
		// const c1 = cursors.get("cursor1");
		// const c2 = cursors.get("cursor2");
		// if (c1 && c2) {
		// 	const startIndex = Math.min(c1.chartIndex, c2.chartIndex);
		// 	const endIndex = Math.max(c1.chartIndex, c2.chartIndex);
		// 	for (const chartId of chartRefs.keys()) {
		// 		if (isNaN(chartId)) {
		// 			continue;
		// 		} else {
		// 			const chart = chartRefs.get(chartId)?.current;
		// 			if (!chart) {
		// 				continue;
		// 			}

		// 			const annotations =
		// 				chart.options.plugins.annotation.annotations;
		// 			if (chartId >= startIndex && chartId <= endIndex) {
		// 				annotations.cursor1 = {
		// 					type: "line",
		// 					xMin: c1.pos,
		// 					xMax: c1.pos,
		// 					index: c1.index,
		// 					borderWidth: c1.chartIndex == chartId ? 2 : 1,
		// 					backgroundColor: "black",
		// 				};
		// 				annotations.cursor2 = {
		// 					type: "line",
		// 					xMin: c2.pos,
		// 					xMax: c2.pos,
		// 					index: c2.index,
		// 					borderWidth: c2.chartIndex == chartId ? 2 : 1,
		// 					borderDash: [4, 2],
		// 					backgroundColor: "black",
		// 				};
		// 				annotations.highlight = {
		// 					type: "box",
		// 					xMin: Math.min(c1.pos, c2.pos),
		// 					xMax: Math.max(c1.pos, c2.pos),
		// 					yMin: -Infinity,
		// 					yMax: Infinity,
		// 					borderWidth: 0,
		// 					backgroundColor: colors["ars-blue-700"] + "40",
		// 				};
		// 			} else {
		// 				annotations.cursor1 = undefined;
		// 				annotations.cursor2 = undefined;
		// 				annotations.highlight = undefined;
		// 			}
		// 		}
		// 	}
		// } else if (c1) {
		// 	for (const chartId of chartRefs.keys()) {
		// 		if (isNaN(chartId)) {
		// 			continue;
		// 		} else {
		// 			const chart = chartRefs.get(chartId)?.current;
		// 			if (!chart) {
		// 				continue;
		// 			}

		// 			const annotations =
		// 				chart.options.plugins.annotation.annotations;
		// 			if (chartId === c1.chartIndex) {
		// 				annotations.cursor1 = {
		// 					type: "line",
		// 					xMin: c1.pos,
		// 					xMax: c1.pos,
		// 					index: c1.index,
		// 					borderWidth: c1.chartIndex == chartId ? 2 : 1,
		// 					backgroundColor: "black",
		// 				};
		// 			} else {
		// 				annotations.cursor1 = undefined;
		// 			}
		// 			annotations.cursor2 = undefined;
		// 			annotations.highlight = undefined;
		// 		}
		// 	}
		// } else if (c2) {
		// 	for (const chartId of chartRefs.keys()) {
		// 		if (isNaN(chartId)) {
		// 			continue;
		// 		} else {
		// 			const chart = chartRefs.get(chartId)?.current;
		// 			if (!chart) {
		// 				continue;
		// 			}

		// 			const annotations =
		// 				chart.options.plugins.annotation.annotations;
		// 			if (chartId === c2.chartIndex) {
		// 				annotations.cursor2 = {
		// 					type: "line",
		// 					xMin: c2.pos,
		// 					xMax: c2.pos,
		// 					index: c2.index,
		// 					borderWidth: c2.chartIndex == chartId ? 2 : 1,
		// 					backgroundColor: "black",
		// 				};
		// 			} else {
		// 				annotations.cursor2 = undefined;
		// 			}
		// 			annotations.cursor1 = undefined;
		// 			annotations.highlight = undefined;
		// 		}
		// 	}
		// } else {
		// 	for (const chartId of chartRefs.keys()) {
		// 		if (isNaN(chartId)) {
		// 			continue;
		// 		} else {
		// 			const chart = chartRefs.get(chartId)?.current;
		// 			if (!chart) {
		// 				continue;
		// 			}
		// 			const annotations =
		// 				chart.options.plugins.annotation.annotations;
		// 			annotations.cursor1 = undefined;
		// 			annotations.cursor2 = undefined;
		// 			annotations.highlight = undefined;
		// 		}
		// 	}
		// }

		updateHighlightsWithNewCursors();
	};

	const stepCursor = (chartIndex, cursorId, direction) => {
		const c1 = cursors.get("cursor1");
		const c2 = cursors.get("cursor2");
		const cursor = cursorId === "cursor1" ? c1 : c2;
		if (!cursor) {
			return;
		}
		if (c1 && c2) {
			const startIndex = Math.min(c1.chartIndex, c2.chartIndex);
			const endIndex = Math.max(c1.chartIndex, c2.chartIndex);
			if (chartIndex < startIndex || chartIndex > endIndex) {
				return;
			}
		} else if (c1) {
			if (chartIndex !== c1.chartIndex) {
				return;
			}
		} else if (c2) {
			if (chartIndex !== c2.chartIndex) {
				return;
			}
		} else {
			return;
		}

		const targetChartIndex = cursor.chartIndex;
		const chart = chartRefs.get(targetChartIndex)?.current;
		if (!chart) return;
		const timestamps = chart.scales.x.getDataTimestamps();
		if (!timestamps) return;
		if (cursor) {
			const index = Number(cursor.index);
			if (direction === 1) {
				if (index >= timestamps.length) {
					return;
				} else {
					moveCursor(
						targetChartIndex,
						cursorId,
						timestamps[index + 1],
						index + 1,
					);
				}
			} else if (direction === -1) {
				if (index <= 0) {
					return;
				} else {
					moveCursor(
						targetChartIndex,
						cursorId,
						timestamps[index - 1],
						index - 1,
					);
				}
			}
		}
	};

	const updateHighlightsWithNewCursors = () => {
		if (!data?._timestamps) return;
		const c1 = cursors.get("cursor1");
		const c2 = cursors.get("cursor2");
		const newHighlighted = {};
		const highlightedPars = [];
		if (c1 && c2) {
			const startIndex = Math.min(c1.chartIndex, c2.chartIndex);
			const endIndex = Math.max(c1.chartIndex, c2.chartIndex);
			const left = Math.min(c1.pos, c2.pos);
			const right = Math.max(c1.pos, c2.pos) + (60*60*1000*(isCalPlot() ? 24 : 1));
			for (const chartId of chartRefs.keys()) {
				if (isNaN(chartId)) {
					continue;
				} else {
					const chart = chartRefs.get(chartId)?.current;
					if (!chart) {
						continue;
					}
					if (chartId >= startIndex && chartId <= endIndex) {
						for (let ts of chart.scales.x.getDataTimestamps()) {
							if (ts < left) continue;
							else if (ts >= right) break;
							else {
								let formattedTs = moment.utc(ts).format("YYYY-MM-DDTHH:mm:ss.SSSZ");
								newHighlighted[formattedTs] = (newHighlighted[formattedTs] || []).concat(chart.data.datasets.map(
									(dataset) => {
										if (dataset.calType) {
											const {parCode, calType, valType} = dataset;
											return [parCode, calType, valType];
										} else {
											return dataset.label;
										}
									}
								));
							}
						}
					}
				}
			}

			// if (highlightedPars.length) {
			// 	const interval =
			// 		1000 * (data._timestamps[1] - data._timestamps[0]);
			// 	let left = Math.min(c1.pos, c2.pos);
			// 	let right = Math.max(c1.pos, c2.pos);
			// 	for (let ts of chart.getDataTimestamps())
			// 	for (let i = left; i <= right; i += interval) {
			// 		newHighlighted[
			// 			moment.utc(i).format("YYYY-MM-DDTHH:mm:ss.SSSZ")
			// 		] = [...highlightedPars];
			// 	}
			// }
		} else if (c1) {
			for (const chartId of chartRefs.keys()) {
				if (chartId === c1.chartIndex) {
					const chart = chartRefs.get(chartId)?.current;
					if (!chart) {
						break;
					}
					const left = c1.pos;
					const right = c1.pos + (60*60*1000*(isCalPlot() ? 24 : 1));
					for (let ts of chart.scales.x.getDataTimestamps()) {
						if (ts < left) continue;
						else if (ts >= right) break;
						else {
							let formattedTs = moment.utc(ts).format("YYYY-MM-DDTHH:mm:ss.SSSZ");
							newHighlighted[formattedTs] = (newHighlighted[formattedTs] || []).concat(chart.data.datasets.map(
								(dataset) => {
									if (dataset.calType) {
										const {parCode, calType, valType} = dataset;
										return [parCode, calType, valType];
									} else {
										return dataset.label;
									}
								}
							));
						}
					}
					break;
				}
			}
		} else if (c2) {
			for (const chartId of chartRefs.keys()) {
				if (chartId === c2.chartIndex) {
					const chart = chartRefs.get(chartId)?.current;
					if (!chart) {
						break;
					}
					const left = c2.pos;
					const right = c2.pos + (60*60*1000*(isCalPlot() ? 24 : 1));
					for (let ts of chart.scales.x.getDataTimestamps()) {
						if (ts < left) continue;
						else if (ts >= right) break;
						else {
							let formattedTs = moment.utc(ts).format("YYYY-MM-DDTHH:mm:ss.SSSZ");
							newHighlighted[formattedTs] = (newHighlighted[formattedTs] || []).concat(chart.data.datasets.map(
								(dataset) => {
									if (dataset.calType) {
										const {parCode, calType, valType} = dataset;
										return [parCode, calType, valType];
									} else {
										return dataset.label;
									}
								}
							));
						}
					}
					break;
				}
			}
		}

		setHighlighted(newHighlighted);
	};

	const zoomAll = (xMin, xMax) => {
		zoomOthers(null, xMin, xMax);
	};

	const zoomOthers = (chartIndex, xMin, xMax) => {
		chartZooms.set("x", { min: xMin, max: xMax });
		for (let index of chartRefs.keys()) {
			if (index === chartIndex) {
				continue;
			}
			const chart = chartRefs.get(index)?.current || null;
			if (chart) {
				const chartOriginalScales = originalScales.get(index);
				if (chartOriginalScales && !chartOriginalScales["x"]) {
					chartOriginalScales["x"] = {
						min: chart.scales.x.min,
						max: chart.scales.x.max,
					};
					chart.originalScales = {
						...chart.originalScales,
						x: {
							min: chart.scales.x.min,
							max: chart.scales.x.max,
						},
					};
				}
				chart.zoomScale("x", { min: xMin, max: xMax });
			} else {
				chartRefs.delete(index);
			}
		}
	};

	const getDatasetType = (type) => {
		if (type === "bar") return type;
		else return "line";
	};

	const getDatasetLineWidth = (type) => {
		return type === "bar" || type === "point" ? 0 : 0.5;
	};

	const getDatasetBackgroundColor = (type, color) => {
		return type === "bar" ? "#" + color : "transparent";
	};

	const datasetHasPoints = (type) => {
		if (type === "point" || type === "line-point") return true;
		else return false;
	};

	const getDatasetSymbol = (symbol) => {
		if (!symbol) return "circle";
		switch (symbol.toUpperCase()) {
			case "X":
				return "crossRot";
			case "T":
				return "triangle";
			default:
				return "circle";
		}
	};

	const generateDataset = (
		parData,
		parCode,
		parUnit,
		type,
		color,
		symbol,
		rawPrec,
		timestamps,
		yAxisId = "y",
		lineWidth,
	) => {
		let lineData = timestamps.map((timestamp) => ({
			x: timestamp * 1000,
			y: parData[timestamp] ? parData[timestamp][0] : null,
		}));
		let lineFlags = timestamps.map((timestamp) =>
			parData[timestamp] ? parData[timestamp][1] : null,
		);

		for (let i = lineData.length - 1; i > 0; i--) {
			const point = lineData[i];
			const nextPoint = lineData[i - 1];
			if (
				(point.y === -999 || point.y === null) &&
				nextPoint.y !== -999 &&
				nextPoint.y !== null
			) {
				point.y = nextPoint.y;
			}
		}

		const dataset = {
			type: getDatasetType(type),
			label: parCode,
			borderColor: "#" + color,
			borderWidth: lineWidth || getDatasetLineWidth(type),
			backgroundColor: getDatasetBackgroundColor(type, color),
			fill: false,
			data: lineData,
			flags: lineFlags,
			xAxisID: "x",
			yAxisID: yAxisId,
			tension: 0,
			pointRadius: datasetHasPoints(type) ? 1 : 0,
			pointStyle: datasetHasPoints(type)
				? getDatasetSymbol(symbol)
				: null,
			pointBorderWidth: 1.2,
			pointBackgroundColor: getDatasetBackgroundColor(type, color),
			categoryPercentage: 0.9,
			barPercentage: 0.9,
			min: getMin(
				lineData.map((point) =>
					point.y === -999 || point.y === null ? 0 : point.y,
				),
			),
			max: getMax(lineData.map((point) => point.y)),

			tooltip: {
				callbacks: {
					label: (tooltipItem) => {
						return `${parCode}: ${Number(tooltipItem.formattedValue).toFixed(rawPrec)} ${parUnit}`;
					},
					afterLabel: (tooltipItem) => {
						return lineFlags[tooltipItem.dataIndex]
							? `${parCode +
							(parCode.match(/_VAL$/) ? "_CODE" : "_FLAG")
							}: ${lineFlags[tooltipItem.dataIndex]}`
							: null;
					},
				},
			},
		};
		return dataset;
	};

	const generateCalDataset = (
		parData,
		parCode,
		calType,
		valType,
		parUnit,
		type,
		color,
		symbol,
		rawPrec,
		timestamps,
		yAxisId = "y",
		lineWidth,
	) => {
		let lineData = Object.entries(parData).map(([key, value]) => ({
			x: Number(key) * 1000,
			y: value[0] === -999 ? null : value[0],
		}));
		let lineFlags = Object.entries(parData).map(([key, value]) => ({
			x: Number(key) * 1000,
			y: value[1],
		}));

		const dataset = {
			type: getDatasetType(type),
			label: `${parCode} ${calType} (${valType})`,
			parCode: parCode,
			calType: calType,
			valType: valType,
			borderColor: "#" + color,
			borderWidth: 0,
			backgroundColor: getDatasetBackgroundColor(type, color),
			fill: false,
			data: lineData,
			xAxisID: "x",
			yAxisID: yAxisId,
			tension: 0,
			pointRadius: 2,
			pointStyle: "circle",
			pointBorderWidth: 1.2,
			pointBackgroundColor: '#' + color,
			categoryPercentage: 0.9,
			barPercentage: 0.9,
			min: getMin(
				lineData.map((point) => (point.y === -999 ? 0 : point.y)),
			),
			max: getMax(lineData.map((point) => point.y)),

			tooltip: {
				callbacks: {
					label: (tooltipItem) => {
						return `${parCode} ${calType} (${valType}): ${Number(tooltipItem.formattedValue).toFixed(rawPrec)} ${parUnit}`;
					},
					afterLabel: (tooltipItem) => {
						return lineFlags[tooltipItem.dataIndex]?.y
							? `${parCode +
							(parCode.match(/_VAL$/) ? "_CODE" : "_FLAG")
							} ${calType} (${valType}): ${lineFlags[tooltipItem.dataIndex].y
							}`
							: null;
					},
				},
			},

			datalabels: {
				display: showCalDataLabels,
				align: valType === "INDICATED" ? "left" : "right",
				offset: 4,
				clip: false,
				color: "#" + color.substring(0, 6),
				font: {
					size: 10,
				},
				formatter: (value, context) => {
					return value.y.toFixed(rawPrec);
				},
			},
		};
		return dataset;
	};

	const generatePercentDiffDataset = (calPar, parameterList, timestamps) => {
		if (!calPar || !parameterList || !timestamps) return null;
		const [parCode, calType, valType] = calPar;
		const isValidated = parCode.match(/_VAL$/) !== null;
		const actualPar = parameterList.find((par) =>
			isValidated
				? par.parCode.match(/_VAL$/) && par.valType === "ACTUAL"
				: !par.parCode.match(/_VAL$/) && par.valType === "ACTUAL",
		);
		const indicatedPar = parameterList.find((par) =>
			isValidated
				? par.parCode.match(/_VAL$/) && par.valType === "INDICATED"
				: !par.parCode.match(/_VAL$/) && par.valType === "INDICATED",
		);

		let lineData = [];
		let min = Infinity;
		let max = -Infinity;
		if (actualPar && indicatedPar) {
			const actualCalPar = [
				actualPar.parCode,
				actualPar.calType,
				actualPar.valType,
			];
			const indicatedCalPar = [
				indicatedPar.parCode,
				indicatedPar.calType,
				indicatedPar.valType,
			];
			const actualData = data[actualCalPar],
				indicatedData = data[indicatedCalPar];

			if (actualData && indicatedData) {
				lineData = Object.keys(actualData)
					.sort()
					.filter((key) => key !== "columnIndex")
					.map((timestamp) => {
						const pDiffVal =
							isNumeric(
								actualData[timestamp]
									? actualData[timestamp][0]
									: null,
							) &&
								isNumeric(
									indicatedData[timestamp]
										? indicatedData[timestamp][0]
										: null,
								)
								? Math.round(
									(10000 *
										(indicatedData[timestamp][0] -
											actualData[timestamp][0])) /
									actualData[timestamp][0],
								) / 100
								: null;
						if (pDiffVal < min) min = pDiffVal;
						if (pDiffVal > max) max = pDiffVal;
						return {
							x: timestamp * 1000,
							y: pDiffVal,
						};
					});
			}
		}

		const dataset = {
			type: "line",
			label: "%Diff",
			borderColor: "#000000",
			borderWidth: 0.5,
			fill: false,
			data: lineData,
			xAxisID: "x",
			yAxisID: "y1",
			tension: 0,
			pointRadius: 1,
			// pointStyle: datasetHasPoints(type)
			//     ? getDatasetSymbol(symbol)
			//     : null,
			pointBorderWidth: 1.2,
			pointBackgroundColor: "#000000",
			// categoryPercentage: 0.9,
			// barPercentage: 0.9,
			spanGaps: true,
			min: min,
			max: max,

			tooltip: {
				callbacks: {
					label: (tooltipItem) => {
						return `%Diff: ${tooltipItem.formattedValue}%`;
					},
				},
			},

			datalabels: {
				display: true,
				align: "top",
				offset: -2,
				clip: false,
				color: "#000000",
				font: {
					size: 10,
				},
				formatter: (value, context) => {
					return value.y.toFixed(2);
				},
			},
		};
		return dataset;
	};

	const generateDiffDataset = (calPar, parameterList, timestamps) => {
		if (!calPar || !parameterList || !timestamps) return null;
		const [parCode, calType, valType] = calPar;
		const isValidated = parCode.match(/_VAL$/) !== null;
		const actualPar = parameterList.find((par) =>
			isValidated
				? par.parCode.match(/_VAL$/) && par.valType === "ACTUAL"
				: !par.parCode.match(/_VAL$/) && par.valType === "ACTUAL",
		);
		const indicatedPar = parameterList.find((par) =>
			isValidated
				? par.parCode.match(/_VAL$/) && par.valType === "INDICATED"
				: !par.parCode.match(/_VAL$/) && par.valType === "INDICATED",
		);

		let lineData = [];
		let min = Infinity;
		let max = -Infinity;
		if (actualPar && indicatedPar) {
			const actualCalPar = [
				actualPar.parCode,
				actualPar.calType,
				actualPar.valType,
			];
			const indicatedCalPar = [
				indicatedPar.parCode,
				indicatedPar.calType,
				indicatedPar.valType,
			];
			const actualData = data[actualCalPar],
				indicatedData = data[indicatedCalPar];

			if (actualData && indicatedData) {
				lineData = Object.keys(actualData)
					.sort()
					.filter((key) => key !== "columnIndex")
					.map((timestamp) => {
						const diffVal =
							isNumeric(
								actualData[timestamp]
									? actualData[timestamp][0]
									: null,
							) &&
								isNumeric(
									indicatedData[timestamp]
										? indicatedData[timestamp][0]
										: null,
								)
								? indicatedData[timestamp][0] -
								actualData[timestamp][0]
								: null;
						if (diffVal > max) max = diffVal;
						if (diffVal < min) min = diffVal;
						return {
							x: timestamp * 1000,
							y: diffVal,
						};
					});
			}
		}

		const dataset = {
			type: "line",
			label: "Diff",
			borderColor: "#000000",
			borderWidth: 0.5,
			fill: false,
			data: lineData,
			xAxisID: "x",
			yAxisID: "y1",
			tension: 0,
			pointRadius: 1,
			// pointStyle: datasetHasPoints(type)
			//     ? getDatasetSymbol(symbol)
			//     : null,
			pointBorderWidth: 1.2,
			pointBackgroundColor: "#000000",
			// categoryPercentage: 0.9,
			// barPercentage: 0.9,
			spanGaps: true,
			min: min,
			max: max,

			tooltip: {
				callbacks: {
					label: (tooltipItem) => {
						return `Diff: ${tooltipItem.formattedValue} ${actualPar.unit}`;
					},
				},
			},

			datalabels: {
				display: true,
				align: "top",
				offset: -2,
				clip: false,
				color: "#000000",
				font: {
					size: 10,
				},
				formatter: (value, context) => {
					return value.y.toFixed(2);
				},
			},
		};

		return dataset;
	};

	const getMin = (data) => {
		if (data.length === 0) return -Infinity;
		else if (data.length === 1) return data[0];
		else {
			let min = data[0];
			for (let i = 0; i < data.length; i++) {
				if (data[i] < min) min = data[i];
			}
			return min;
		}
	};

	const getMax = (data) => {
		if (data.length === 0) return Infinity;
		else if (data.length === 1) return data[0];
		else {
			let max = data[0];
			for (let i = 0; i < data.length; i++) {
				if (data[i] > max) max = data[i];
			}
			return max;
		}
	};

	const handleWheel = (event) => {
		const target = event.target;
		if (event.ctrlKey && target.tagName !== "canvas") {
			event.preventDefault();
			return;
		}
	};

	const isCalPlot = () => {
		if (!config) return false;
		return ["RAWCAL", "VALCAL", "VMCAL"].includes(config?.dataType || "");
	};

	return (
		<ValidationPlotContext.Provider
			value={{
				chartRefs,
				chartZooms,
				originalScaleMap: originalScales,
				zoomAll,
				zoomOthers,
				cursors,
				moveCursor,
				stepCursor,
				clearCursors,
				plugins,
				printable,
				isCalPlot,
			}}
		>
			<div
				id="validation-plot"
				className={
					"stackplot-wrapper" + (printable ? " printable" : "")
				}
				ref={stackPlotRef}
			>
				<div
					className="stackplot-cal-label-toggle"
					hidden={!isCalPlot() || printable}
				>
					<span>Show Data Labels</span>
					<input
						type="checkbox"
						checked={showCalDataLabels}
						onClick={toggleCalDataLabels}
					/>
				</div>
				<div className="stackplot-pages-container-wrapper">
					<div className="stackplot-pages-container">
						{isCalPlot() ? generateCalPages() : generatePages()}
					</div>
					<StackPlotXAxis
						data={{
							datasets: [],
							labels: getLabelsFromTimestamps(data?._timestamps),
						}}
						xIndex={0}
					/>
				</div>
				<div
					className="stackplot-footnote"
					style={{ height: STACKPLOT_FOOTNOTE_HEIGHT }}
					hidden={printable || !config?.footnote}
				>
					{config?.footnote || ""}
				</div>
			</div>
		</ValidationPlotContext.Provider>
	);
}

function StackPlotChart({
	chartIndex,
	config,
	data,
	options,
	popoverCallback,
	chartSpacing = 20,
	chartHighlights,
	autoYAxis,
	fixedYScales
}) {
	const [localConfig, setLocalConfig] = useState(config);
	const [originalScales, setOriginalScales] = useState(new Map());
	const [colorCoding, setColorCoding] = useState({});
	const chartRef = useRef(null);

	const {
		chartRefs,
		chartZooms,
		originalScaleMap,
		zoomAll,
		zoomOthers,
		cursors,
		moveCursor,
		stepCursor,
		clearCursors,
		plugins,
		printable,
		isCalPlot,
	} = useContext(ValidationPlotContext);

	useEffect(() => {
		originalScales.clear();
		if (chartZooms) {
			chartZooms.delete(chartIndex);
		}
		setLocalConfig(config);
		if (chartRef.current) {
			chartRef.current.update();
		}
	}, [config]);

	useEffect(() => {
		originalScales.clear();
		if (chartZooms) {
			chartZooms.delete(chartIndex);
		}
		if (chartRef.current) {
			const chart = chartRef.current;
			chart.update();
		}
		setColorCoding(getChartColorCoding());
	}, [data]);

	useEffect(() => {
		if (chartRef.current) {
			const chart = chartRef.current;
			chart.update();
		}
	}, [autoYAxis, fixedYScales]);

	useEffect(() => {
		if (chartRef.current?.canvas) {
			chartRefs.set(chartIndex, chartRef);
			chartRef.current.canvas.tabIndex = 0;
			originalScaleMap.set(chartIndex, originalScales);
		} else {
			chartRefs.delete(chartIndex);
			originalScaleMap.delete(chartIndex);
		}
	}, [chartRef.current]);

	const getLeftMin = () => {
		if (!localConfig?.parameters?.length) return 0;

		if (chartZooms?.get(chartIndex)?.y !== undefined) {
			return chartZooms.get(chartIndex).y.min;
		}

		if (isCalPlot()) {
			const chartPar = localConfig.parameters[0];
			const chartParameter = chartPar.parCode.split('-')[0];
			const chartCalType = chartPar.calType;

			if (!autoYAxis && fixedYScales[[chartParameter, chartCalType]]) {
				return fixedYScales[[chartParameter, chartCalType]].min;
			}
		} else {
			const chartParameter = localConfig.parameters[0].parCode.split('-')[0];

			if (!autoYAxis && fixedYScales[chartParameter]) {
				return fixedYScales[chartParameter].min;
			}
		}

		if (localConfig.leftMin === undefined || localConfig.leftMin === null) {
			if (localConfig.parameters.length) {
				let min = Infinity;
				for (let dataset of data.datasets) {
					const parMin = dataset.min;
					if (parMin < min) {
						min = parMin;
					}
				}
				return min;
			} else {
				return 0;
			}
		} else {
			return localConfig.leftMin;
		}
	};

	const getLeftMax = () => {
		if (!localConfig?.parameters?.length) return 0;

		if (chartZooms?.get(chartIndex)?.y !== undefined) {
			return chartZooms.get(chartIndex).y.max;
		}

		if (isCalPlot()) {
			const chartPar = localConfig.parameters[0];
			const chartParameter = chartPar.parCode.split('-')[0];
			const chartCalType = chartPar.calType;

			if (!autoYAxis && fixedYScales[[chartParameter, chartCalType]]) {
				return fixedYScales[[chartParameter, chartCalType]].max;
			}
		} else {
			const chartParameter = localConfig.parameters[0].parCode.split('-')[0];

			if (!autoYAxis && fixedYScales[chartParameter]) {
				return fixedYScales[chartParameter].max;
			}
		}

		if (localConfig.leftMax === undefined || localConfig.leftMax === null) {
			if (localConfig.parameters.length) {
				let max = -Infinity;
				for (let dataset of data.datasets) {
					const parMax = dataset.max;
					if (parMax > max) {
						max = parMax;
					}
				}
				return max;
			} else {
				return 0;
			}
		} else {
			return localConfig.leftMax;
		}
	};

	const getRightMin = () => {
		if (chartZooms?.get(chartIndex)?.y1 !== undefined) {
			return chartZooms.get(chartIndex).y1.min;
		}

		if (
			localConfig.rightMax === undefined ||
			localConfig.rightMax === null
		) {
			const rightPars = localConfig.parameters.filter(
				(parameter) => parameter.side === "R",
			);
			if (rightPars.length) {
				let min = Infinity;
				for (let dataset of data.datasets) {
					if (dataset.yAxisID === "y1") {
						if (dataset.min < min) {
							min = dataset.min;
						}
					}
				}
				return Number(min);
			} else {
				return 0;
			}
		} else {
			return localConfig.rightMin;
		}
	};

	const getRightMax = () => {
		if (chartZooms?.get(chartIndex)?.y1 !== undefined) {
			return chartZooms.get(chartIndex).y1.max;
		}

		if (
			localConfig.rightMax === undefined ||
			localConfig.rightMax === null
		) {
			const rightPars = localConfig.parameters.filter(
				(parameter) => parameter.side === "R",
			);
			if (rightPars.length) {
				let max = -Infinity;
				for (let dataset of data.datasets) {
					if (dataset.yAxisID === "y1") {
						if (dataset.max > max) {
							max = dataset.max;
						}
					}
				}
				return Number(max) + (isCalPlot() ? 80 : 0);
			} else {
				return 0 + (isCalPlot() ? 80 : 0);
			}
		} else {
			return localConfig.rightMax + (isCalPlot() ? 80 : 0);
		}
	};

	function zoomDelta(scale, zoom, center) {
		const range = scale.max - scale.min;
		const newRange = range * (zoom - 1);

		const centerPoint = scale.isHorizontal() ? center.x : center.y;
		// `scale.getValueForPixel()` can return a value less than the `scale.min` or
		// greater than `scale.max` when `centerPoint` is outside chartArea.
		const minPercent = Math.max(
			0,
			Math.min(
				1,
				(scale.getValueForPixel(centerPoint) - scale.min) / range || 0,
			),
		);

		const maxPercent = 1 - minPercent;

		return {
			min: newRange * minPercent,
			max: newRange * maxPercent,
		};
	}

	function updateRange(scale, { min, max }, limits, zoom = false) {
		const { id, axis, options: scaleOpts } = scale;

		const scaleLimits = limits?.get(id) || {};
		const { minRange = 0 } = scaleLimits;
		const minLimit =
			!scale.isHorizontal() || scaleLimits["min"] === undefined
				? -Infinity
				: scaleLimits["min"];
		const maxLimit =
			!scale.isHorizontal() || scaleLimits["max"] === undefined
				? Infinity
				: scaleLimits["max"];

		const range = zoom
			? Math.max(max - min, minRange)
			: scale.max - scale.min;
		const offset = (range - max + min) / 2;
		min -= offset;
		max += offset;

		if (min < minLimit) {
			min = minLimit;
			max = Math.min(minLimit + range, maxLimit);
		} else if (max > maxLimit) {
			max = maxLimit;
			min = Math.max(maxLimit - range, minLimit);
		}
		scaleOpts.min = min;
		scaleOpts.max = max;

		// return true if the scale range is changed
		return scale.parse(min) !== scale.min || scale.parse(max) !== scale.max;
	}

	function zoomNumericalScale(scale, zoom, center, limits) {
		const delta = zoomDelta(scale, zoom, center);
		const newRange = {
			min: scale.min + delta.min,
			max: scale.max - delta.max,
		};
		return updateRange(scale, newRange, limits, true);
	}

	const doZoom = (chart, x, y, point) => {
		const xEnabled = x !== 1;
		const yEnabled = y !== 1;

		for (let scaleId in chart.scales) {
			const scale = chart.scales[scaleId];
			if (scale.isHorizontal() && xEnabled) {
				zoomNumericalScale(scale, x, point, originalScales);
			} else if (!scale.isHorizontal() && yEnabled) {
				if (isCalPlot() && scaleId === "y1") {
					continue;
				}
				zoomNumericalScale(scale, y, point, originalScales);
			}
		}

		chart.update();
		const xMin = chart.scales.x.min;
		const xMax = chart.scales.x.max;
		zoomOthers(chartIndex, xMin, xMax);
	};

	const getColorForCode = (valCode, loggerFlag) => {
		if (GREEN_CODES.includes(valCode)) {
			return "green";
		} else {
			if (loggerFlag?.match("<")) {
				return "yellow";
			} else {
				return "red";
			}
		}
	};

	const getChartColorCoding = () => {
		const colorCoding = {};
		if (!data?.datasets?.length) return colorCoding;
		let interval;
		for (let dataset of data.datasets) {
			if (dataset.data?.length > 1) {
				interval = dataset.data[1].x - dataset.data[0].x;
				break;
			}
		}
		if (!interval) return colorCoding;
		const colorLists = {
			green: [],
			yellow: [],
			red: [],
		};
		for (const dataset of data.datasets) {
			if (dataset.label?.match(".*_VAL")) {
				const rawDataset = data.datasets.find(
					(ds) =>
						ds.label ===
						dataset.label.substr(0, dataset.label.length - 4),
				);
				if (!rawDataset) continue;
				const rawFlags = rawDataset.flags;
				const lineData = dataset.data;
				const lineFlags = dataset.flags;
				for (let i = 0; i < lineFlags.length; i++) {
					if (lineFlags[i]) {
						const color = getColorForCode(
							lineFlags[i],
							rawFlags[i],
						);
						if (color) {
							colorLists[color].push(lineData[i].x);
						}
					}
				}
			}
		}
		for (let color in colorLists) {
			const colorTimestamps = colorLists[color];
			if (!colorTimestamps?.length) continue;
			colorTimestamps.sort();
			let l = 0,
				r;
			const colorRanges = [];
			// const interval =
			// 	data.datasets[0].data[1].x - data.datasets[0].data[0].x;
			for (let i = 1; i < colorTimestamps.length; i++) {
				if (colorTimestamps[i] - colorTimestamps[i - 1] !== interval) {
					r = i - 1;
					colorRanges.push([colorTimestamps[l], colorTimestamps[r]]);
					l = i;
				}
			}
			colorRanges.push([
				colorTimestamps[l],
				colorTimestamps[colorTimestamps.length - 1],
			]);

			for (let i = 0; i < colorRanges.length; i++) {
				const range = colorRanges[i];

				colorCoding[`${color}-${i}`] = {
					type: "box",
					xMin: range[0],
					xMax: range[1] + interval,
					yMin: -Infinity,
					yMax: Infinity,
					borderWidth: 0,
					backgroundColor: colors[`ars-${color}-600`] + "40",
				};
				// if (range[0] == range[1]) {
				// 	// 1 data point wide
				// 	colorCoding[`${color}-${i}`] = {
				// 		type: "line",
				// 		xMin: range[0],
				// 		xMax: range[1],
				// 		yMin: -Infinity,
				// 		yMax: Infinity,
				// 		borderWidth: 2,
				// 		borderColor: colors[`ars-${color}-600`],
				// 	};
				// } else {
				// 	colorCoding[`${color}-${i}`] = {
				// 		type: "box",
				// 		xMin: range[0],
				// 		xMax: range[1],
				// 		yMin: -Infinity,
				// 		yMax: Infinity,
				// 		borderWidth: 0,
				// 		backgroundColor: colors[`ars-${color}-600`] + "40",
				// 	};
				// }
			}
		}
		return colorCoding;
	};

	const buildOptions = () => {
		let newOptions = JSON.parse(JSON.stringify(options));
		newOptions.chartIndex = chartIndex;
		newOptions.clip = false;

		newOptions.scales.y.afterFit = (axis) => {
			axis.width = Y_AXIS_LABEL_WIDTH;
		};

		if (chartZooms?.get("x")) {
			newOptions.scales.x.min = chartZooms.get("x").min;
			newOptions.scales.x.max = chartZooms.get("x").max;
		} else {
			const labels = data.labels;
			const minTs = moment(labels[0]).valueOf(),
				maxTs = moment(labels[labels.length - 1]).valueOf();

			newOptions.scales.x.min = minTs;
			newOptions.scales.x.max = maxTs;
		}
		if (isCalPlot()) {
			newOptions.scales.x.time.minUnit = "day";
		}

		newOptions.scales.y.min = getLeftMin();
		newOptions.scales.y.max = getLeftMax();
		newOptions.scales.y.originalMin = newOptions.scales.y.min;
		newOptions.scales.y.originalMax = newOptions.scales.y.max;
		if (localConfig.hasRightSide) {
			newOptions.scales.y1 = {
				type: "linear",
				position: "right",
				display: true,
				ticks: {
					font: STACKPLOT_AXIS_FONT,
				},
				suggestedMin: 0,
				suggestedMax: 1,
				min: getRightMin(),
				max: getRightMax(),
				grid: {
					drawOnChartArea: false,
				},
				afterFit: (axis) => {
					axis.width = Y_AXIS_LABEL_WIDTH;
				},
			};
			newOptions.layout.padding.right = 0;
		}

		newOptions.plugins.tooltip.callbacks = {};
		newOptions.plugins.tooltip.callbacks.title = (tooltipItems) => {
			const timestamp = moment(
				tooltipItems[0].label,
				"MMM D, YYYY, HH:mm:ss",
			).seconds(0);
			return timestamp.format("MMM D, YYYY, HH:mm");
		};

		if (newOptions.scales.x && !originalScales.get("x")) {
			originalScales.set("x", {
				min: newOptions.scales.x.min,
				max: newOptions.scales.x.max,
			});
		}
		if (newOptions.scales.y && !originalScales.get("y")) {
			originalScales.set("y", {
				min: newOptions.scales.y.min,
				max: newOptions.scales.y.max,
			});
		}
		if (newOptions.scales.y1 && !originalScales.get("y1")) {
			originalScales.set("y1", {
				min: newOptions.scales.y1.min,
				max: newOptions.scales.y1.max,
			});
		}
		newOptions.originalScales = Object.fromEntries(originalScales);

		newOptions.plugins.zoom.pan.onPan = ({ chart }) => {
			if (chart.options.plugins.zoom.pan.mode === "x") {
				const xMin = chart.scales.x.min;
				const xMax = chart.scales.x.max;

				zoomOthers(chartIndex, xMin, xMax);
			}
		};

		newOptions.plugins.zoom.zoom.onZoomStart = ({
			chart,
			event,
			point,
		}) => {
			if (chart.scales.x && !originalScales.get("x")) {
				originalScales.set("x", {
					min: chart.scales.x.min,
					max: chart.scales.x.max,
				});
			}
			if (chart.scales.y && !originalScales.get("y")) {
				originalScales.set("y", {
					min: chart.scales.y.min,
					max: chart.scales.y.max,
				});
			}
			if (chart.scales.y1 && !originalScales.get("y1")) {
				originalScales.set("y1", {
					min: chart.scales.y1.min,
					max: chart.scales.y1.max,
				});
			}
			chart.originalScales = Object.fromEntries(originalScales);
			if (event.ctrlKey) {
				event.preventDefault();
				const speed =
					1 +
					(event.deltaY >= 0
						? -chart.options.plugins.zoom.zoom.wheel.speed
						: chart.options.plugins.zoom.zoom.wheel.speed);
				// chart.zoom({
				//     x: speed,
				//     y: 1,
				//     focalPoint: point,
				// });
				doZoom(chart, speed, 1, point);
				return false;
			} else if (event.shiftKey) {
				event.preventDefault();
				const speed =
					1 +
					(event.deltaY >= 0
						? -chart.options.plugins.zoom.zoom.wheel.speed
						: chart.options.plugins.zoom.zoom.wheel.speed);
				// chart.zoom({
				//     x: 1,
				//     y: speed,
				//     focalPoint: point,
				// });
				doZoom(chart, 1, speed, point);
				chartZooms.set(chartIndex, {
					y: chart.scales.y
						? {
							min: chart.scales.y.min,
							max: chart.scales.y.max,
						}
						: null,
					y1: chart.scales.y1
						? {
							min: chart.scales.y1.min,
							max: chart.scales.y1.max,
						}
						: null,
				});
				return false;
			} else {
				return false;
			}
		};

		newOptions.plugins.zoom.pan.onPanStart = ({ chart, event }) => {
			if (chart.scales.x && !originalScales.get("x")) {
				originalScales.set("x", {
					min: chart.scales.x.min,
					max: chart.scales.x.max,
				});
			}
			if (chart.scales.y && !originalScales.get("y")) {
				originalScales.set("y", {
					min: chart.scales.y.min,
					max: chart.scales.y.max,
				});
			}
			if (chart.scales.y1 && !originalScales.get("y1")) {
				originalScales.set("y1", {
					min: chart.scales.y1.min,
					max: chart.scales.y1.max,
				});
			}
			chart.originalScales = Object.fromEntries(originalScales);
			chart.isCalPlot = isCalPlot();
			if (event.srcEvent.shiftKey) {
				// event.preventDefault();
				chart.options.plugins.zoom.pan.mode = "y";
				event.deltaX = 0;
				// const speed = event.deltaY;
				// doPan(chart, 0, speed);
			} else {
				// event.preventDefault();
				chart.options.plugins.zoom.pan.mode = "x";
				event.deltaY = 0;
				// const speed = event.deltaX;
				// doPan(chart, speed, 0);
			}
		};

		newOptions.plugins.annotation.annotations = {
			...newOptions.plugins.annotation.annotations,
			...chartHighlights,
			...colorCoding,
		};

		newOptions.plugins.datalabels = { display: false };

		if (printable) {
			newOptions.plugins.customCanvasBackgroundColor = {
				color: "white",
			};
		}

		if (isCalPlot()) {
			newOptions.plugins.decimation = { enabled: false };
			if (newOptions.scales.y1) {
				newOptions.scales.y1.display = false;
				newOptions.plugins.zoom.limits.y1 = {
					min: "original",
					max: "original",
				};
			}
		}

		return newOptions;
	};

	const getLeftLegend = () => {
		if (!localConfig?.parameters) return null;
		const leftParameters = localConfig.parameters.filter(
			(parameter) => parameter.side === "L",
		);
		const legendItems = [];
		for (const par of leftParameters) {
			const legendItem = (
				<span
					style={{ color: "#" + par.color }}
				>{`${par.parCode} (${par.unit})`}</span>
			);
			legendItems.push(legendItem);
		}
		return legendItems;
	};

	const getCenterLegend = () => {
		if (!isCalPlot() || !localConfig?.parameters?.length) return null;
		let calPar;
		for (let par of localConfig.parameters) {
			if (par.valType === "INDICATED") {
				calPar = par;
				break;
			}
		}
		if (!calPar) return null;
		const parameter = calPar.parCode,
			calType = calPar.calType;
		return `${parameter} ${calType}`;
	};

	const getRightLegend = () => {
		if (!localConfig?.parameters?.length || !localConfig?.hasRightSide)
			return null;
		const rightParameters = localConfig.parameters.filter(
			(parameter) => parameter.side === "R",
		);
		const legendItems = [];
		for (const par of rightParameters) {
			const legendItem = (
				<span style={{ color: "#" + par.color }}>{`${par.parCode} (${par.valType === "DIFF" ? getDiffUnit(par) : par.unit
					})`}</span>
			);
			legendItems.push(legendItem);
		}
		console.log(legendItems);
		return legendItems;
	};

	const getDiffUnit = (par) => {
		if (!localConfig?.parameters?.length) return null;
		const indicatedPar = localConfig.parameters.find(
			(parameter) => parameter.valType === "INDICATED",
		);
		return indicatedPar?.unit || null;
	};

	const dblClickPlugin = {
		id: "doubleClickZoomReset",
		defaults: {
			events: ["dblclick"],
		},
		beforeEvent: (chart, args) => {
			const event = args.event;
			if (event.type === "dblclick") {
				if (!chart.originalScales) return;
				if (event.native.shiftKey) {
					if (chart.options.scales.y && chart.originalScales.y) {
						const originalY = chart.originalScales.y;
						chart.zoomScale("y", originalY);
					}
					if (chart.options.scales.y1 && chart.originalScales.y1) {
						const originalY1 = chart.originalScales.y1;
						chart.zoomScale("y1", originalY1);
					}
				} else if (event.native.ctrlKey) {
					if (chart.originalScales.x) {
						zoomAll(
							chart.originalScales.x.min,
							chart.originalScales.x.max,
						);
					}
				}
			}
			return false;
		},
	};

	const xCursorPlugin = {
		id: "xCursorPlugin",
		defaults: {
			events: ["keydown", "click", "blur"],
		},
		beforeEvent: (chart, args) => {
			const event = args.event.native;
			const type = args.event.type;
			const ctrl = event.ctrlKey;
			const cursorId = ctrl ? "cursor2" : "cursor1";
			if (type === "keydown") {
				if (event.shiftKey) return;
				const key = event.code;
				const xIndex =
					chart.options.plugins.annotation.annotations[cursorId]
						?.index || null;
				if (key === "ArrowLeft") {
					stepCursor(chartIndex, cursorId, -1);
				} else if (key === "ArrowRight") {
					stepCursor(chartIndex, cursorId, 1);
				}
			} else if (type === "click") {
				if (event.detail > 1) return;
				if (event.shiftKey) {
					clearCursors();
					return;
				}
				chart.canvas.focus();
				if (event.detail > 1) return;
				const position = getRelativePosition(event, chart);
				const xCoord = chart.scales.x.getValueForPixel(position.x);

				let x = null,
					index = null;
				const dataTimestamps = chart.scales.x.getDataTimestamps();
				for (let i in dataTimestamps) {
					if (i === 0) continue;
					if (dataTimestamps[i] > xCoord) {
						x = dataTimestamps[i - 1];
						index = Number(i) - 1;
						break;
					}
				}
				if (x) {
					moveCursor(chartIndex, cursorId, x, index);
				} else {
					chart.options.plugins.annotation = undefined;
				}
				chart.update();
			}
		},
	};

	const originalLimitsPlugin = {
		id: "originalLimitsPlugin",
		afterUpdate: (chart, args, options) => {
			if (chart.scales.x && !originalScales.get("x")) {
				originalScales.set("x", {
					min: chart.scales.x.min,
					max: chart.scales.x.max,
				});
			}
			if (chart.scales.y && !originalScales.get("y")) {
				originalScales.set("y", {
					min: chart.scales.y.min,
					max: chart.scales.y.max,
				});
			}
			if (chart.scales.y1 && !originalScales.get("y1")) {
				originalScales.set("y1", {
					min: chart.scales.y1.min,
					max: chart.scales.y1.max,
				});
			}
			chart.originalScales = Object.fromEntries(originalScales);
		},
	};

	const getPlugins = () => {
		return [
			dblClickPlugin,
			xCursorPlugin,
			backgroundColorPlugin,
			dataLabelPlugin,
			originalLimitsPlugin,
			...plugins,
		];
	};

	return (
		<div
			id={"stackplot-chart-" + chartIndex}
			className="stackplot-chart-wrapper"
		>
			{/* <div
				className="validation-chart-legend"
				style={{
					height: STACKPLOT_LABEL_HEIGHT,
					marginBottom: STACKPLOT_LABEL_BOTTOM_MARGIN,
					fontSize: STACKPLOT_LABEL_FONT_SIZE,
				}}
			>
				<div className="stackplot-chart-legend-center">
				</div>
			</div> */}
			<div className="validation-chart-legend-left" style={{
				maxHeight:
					(localConfig.chartHeight
						? localConfig.chartHeight
						: 200) + "px",
			}}>
				{getCenterLegend()}
				{getLeftLegend()}
			</div>
			<Chart
				type="line"
				data={data}
				options={buildOptions()}
				ref={chartRef}
				plugins={getPlugins()}
				style={{
					padding: "0 80px",
					maxHeight:
						(localConfig.chartHeight
							? localConfig.chartHeight
							: 200) + "px",
					height:
						(localConfig.chartHeight
							? localConfig.chartHeight
							: 200) + "px",
				}}
				onContextMenu={popoverCallback}
			/>
			<div className="validation-chart-legend-right">
				{getRightLegend()}
			</div>
		</div>
	);
}

function StackPlotXAxis({ data, xIndex, bufferSize = 0 }) {
	const chartRef = useRef(null);

	const { chartRefs, chartZooms, printable } = useContext(
		ValidationPlotContext,
	);

	useEffect(() => {
		if (chartRef.current) {
			chartRefs.set("x-" + xIndex, chartRef);
		}
	}, [chartRef.current]);

	const getOptions = () => {
		const newOptions = { ...xAxisOptions };
		if (chartZooms?.get("x")) {
			newOptions.scales.x.min = chartZooms.get("x").min;
			newOptions.scales.x.max = chartZooms.get("x").max;
		} else {
			const labels = data.labels;
			const minTs = moment(labels[0]).valueOf(),
				maxTs = moment(labels[labels.length - 1]).valueOf();

			newOptions.scales.x.min = minTs;
			newOptions.scales.x.max = maxTs;
		}
		newOptions.plugins.customCanvasBackgroundColor = {
			color: printable ? "white" : "transparent",
		};
		return newOptions;
	};

	return (
		<Chart
			className="stackplot-x-axis"
			data={data}
			options={getOptions()}
			ref={chartRef}
			plugins={[backgroundColorPlugin]}
			style={{
				padding: "0 80px",
				maxHeight: STACKPLOT_X_AXIS_HEIGHT + "px",
				marginBottom: printable ? bufferSize + "px" : 0,
			}}
		/>
	);
}

function StackPlotPage({ title, footnote, pageNumber, charts, numPages }) {
	const { printable } = useContext(ValidationPlotContext);

	const getTitle = () => {
		return title || "";
	};

	const isFirstPage = () => {
		return pageNumber === 1;
	};

	const getCharts = () => {
		return charts || [];
	};

	const getFootnote = () => {
		return footnote || "";
	};

	const getDate = () => {
		return moment().format("MMM D, YYYY");
	};

	return (
		<div className={"stackplot-page" + (printable ? " printable" : "")}>
			<div className="stackplot-page-header">{`${title} (Page ${pageNumber} of ${numPages})`}</div>
			<div
				className="stackplot-title"
				style={{ height: STACKPLOT_TITLE_HEIGHT }}
				hidden={!(printable && isFirstPage())}
			>
				{getTitle()}
			</div>
			{getCharts()}
			<div
				className="stackplot-footnote"
				style={{ height: STACKPLOT_FOOTNOTE_HEIGHT }}
				hidden={!(printable && footnote)}
			>
				{getFootnote()}
			</div>
			<div className="stackplot-page-footer">{`${getDate()}`}</div>
		</div>
	);
}

export default ValidationPlot;
